import { useCallback, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { bidKeys, type PendingFeedbackRowApi } from '@tempmee/awards';
import { Column, Toast, Typography } from '@tempmee/react';
import { useEodBidGate, usePendingBidFeedback } from '~/domain/BestInDental';
import { BidFeedbackDialog } from '../BidFeedbackDialog';
import { BID_TOAST_AUTO_HIDE_MS, BidLoadingState } from '../shared';
import { BidNomineeRow, BidNomineeTable } from './components';

const FEEDBACK_SUBMITTED_MESSAGE = 'Thanks! Your Best in Dental feedback has been submitted.';
const FEEDBACK_UNAVAILABLE_MESSAGE =
  'This feedback is no longer needed — it may have already been submitted.';

interface IPendingFeedbackDialogHostProps {
  cycleId: string;
  row: PendingFeedbackRowApi;
  onClose: () => void;
  onSubmitted: () => void;
  onUnavailable: () => void;
}

/**
 * Fetches the per-pair gate for a clicked row and renders the shared
 * feedback dialog once its questions arrive. The gate is asked AND the
 * feedback submitted as the ROW's office (`x-office: row.officeId`) — for a
 * multi-office admin that is not the selected office, and asking as the
 * wrong office records the wrong reviewer.
 *
 * The gate service fails open to a closed gate on any error, and a closed
 * gate here also arrives when the pair was already reviewed (e.g. by a
 * sibling admin) — either way there is nothing to show, so a settled closed
 * gate reports `onUnavailable` instead of rendering.
 */
const PendingFeedbackDialogHost = ({
  cycleId,
  row,
  onClose,
  onSubmitted,
  onUnavailable,
}: IPendingFeedbackDialogHostProps) => {
  const { data: gate, isFetching } = useEodBidGate(cycleId, row.professionalId, row.officeId);

  const isGateOpen = Boolean(
    gate?.showStep && gate.cycleId && gate.applicantUserId && gate.questions?.length,
  );

  useEffect(() => {
    // `placeholderData` shows a closed gate while the query is in flight —
    // only a SETTLED closed gate means the row is stale.
    if (!isFetching && !isGateOpen) {
      onUnavailable();
    }
  }, [isFetching, isGateOpen, onUnavailable]);

  if (!isGateOpen || !gate?.cycleId || !gate.applicantUserId || !gate.questions?.length) {
    return null;
  }

  return (
    <BidFeedbackDialog
      cycleId={gate.cycleId}
      applicantUserId={gate.applicantUserId}
      applicantName={row.professionalName}
      questions={gate.questions}
      officeId={row.officeId}
      dismissLabel="Cancel"
      dataCy="bid-pending-feedback-dialog"
      onComplete={onClose}
      onSubmitted={onSubmitted}
    />
  );
};

/**
 * CS-5 (GOTU-5643) reminder list on the Today's-Shift dashboard: applicants
 * still awaiting office feedback, one row per (office, professional) pair
 * (shared shifts collapse — awards-service#37, which is why rows carry no
 * shift date). `profession` is the shift code (RDH, DA, DN, FD) from the
 * pair's most recent shift.
 *
 * The CTA opens the shared feedback dialog against the envelope `cycleId`
 * (the server-resolved cycle every submit must target, GOTU-6571). No
 * `jobId` is sent — it is optional provenance the pending rows cannot name.
 * Submitting invalidates the pending list, which removes the row.
 */
export const BidPendingFeedbackTab = () => {
  const { data, isLoading } = usePendingBidFeedback();
  const queryClient = useQueryClient();
  const rows = data?.cycleId ? data.rows : [];
  const [activeRow, setActiveRow] = useState<PendingFeedbackRowApi | null>(null);
  const [toast, setToast] = useState<'submitted' | 'unavailable' | null>(null);

  const handleSubmitted = useCallback(() => {
    setActiveRow(null);
    setToast('submitted');
  }, []);

  const handleUnavailable = useCallback(() => {
    setActiveRow(null);
    setToast('unavailable');
    // The row is stale (already reviewed, window closed, …) — refresh the
    // list so it stops advertising it.
    queryClient.invalidateQueries({ queryKey: bidKeys.pendingFeedback() });
  }, [queryClient]);

  if (isLoading) {
    return <BidLoadingState />;
  }

  if (rows.length === 0) {
    return (
      <Column align="center" py={12} data-cy="bid-pending-feedback-empty">
        <Typography>You&apos;re all caught up on Best in Dental feedback!</Typography>
      </Column>
    );
  }

  return (
    <Column gap={3} width="100%">
      <BidNomineeTable data-cy="bid-pending-feedback-list">
        {rows.map((row) => (
          <BidNomineeRow
            key={`${row.officeId}-${row.professionalId}`}
            name={row.professionalName}
            profession={row.profession}
            officeName={row.officeName}
            avatarUrl={row.profileImage}
            disabled={activeRow !== null}
            onComplete={() => setActiveRow(row)}
            data-cy="bid-pending-feedback-row"
          />
        ))}
      </BidNomineeTable>

      {activeRow && data?.cycleId && (
        <PendingFeedbackDialogHost
          cycleId={data.cycleId}
          row={activeRow}
          onClose={() => setActiveRow(null)}
          onSubmitted={handleSubmitted}
          onUnavailable={handleUnavailable}
        />
      )}

      <Toast
        open={toast !== null}
        severity={toast === 'submitted' ? 'success' : 'info'}
        message={toast === 'submitted' ? FEEDBACK_SUBMITTED_MESSAGE : FEEDBACK_UNAVAILABLE_MESSAGE}
        autoHideDuration={BID_TOAST_AUTO_HIDE_MS}
        onClose={() => setToast(null)}
      />
    </Column>
  );
};
